import { CommonModule } from '@angular/common';
import {
  Component,
  EmbeddedViewRef,
  HostBinding,
  Input,
  OnChanges,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { ValueDevelopmentChartComponent } from '@components/fondsportrait/value-development-chart/value-development-chart.component';
import { HighchartsDirective } from '@utils/charts/highcharts.directive';
import { legacyOptionsTransformer } from '@utils/charts/legacy-chart-options.pipe';
import { TransformChartOptionsPipe } from '@utils/charts/transform-chart-options.pipe';
import * as Highcharts from 'highcharts';
import { SeriesTreemapOptions } from 'highcharts';
import { Options } from 'highcharts/highcharts.src';
import * as Highstock from 'highcharts/highstock';
import { getCellWidgetClasses } from 'src/app/element-handling';
import { getCellContentLabel } from 'src/app/table-handling/util';
import { DocumentElementResponse } from 'src/openapi-client';
import { Renderable } from 'src/openapi-client/model/renderable';
import { DoughnutChartComponent } from '../doughnut-chart/doughnut-chart.component';
import { SimpleTableComponent } from '../tables/simple-table/simple-table.component';
import { SolidTableComponent } from '../tables/solid-table/solid-table.component';
import { TightTableComponent } from '../tables/tight-table/tight-table.component';
import { TreemapChartData, TreemapChartDataSerie as TreemapChartDataSeries } from '../treemap-chart/mapping';
import { TreemapChartComponent } from '../treemap-chart/treemap-chart.component';
import { ChartConstructorPipe } from './chart-constructor.pipe';
import { ChartCssClassesPipe } from './chart-css-classes.pipe';
import { ElementHostOptions, TableType } from './element-host.options';

@Component({
  selector: 'wct-element-host',
  standalone: true,
  imports: [
    CommonModule,
    ValueDevelopmentChartComponent,
    SimpleTableComponent,
    SolidTableComponent,
    TightTableComponent,
    TreemapChartComponent,
    DoughnutChartComponent,

    TransformChartOptionsPipe,
    ChartConstructorPipe,
    ChartCssClassesPipe,

    HighchartsDirective,
  ],
  templateUrl: './element-host.component.html',
  styleUrls: ['./element-host.component.scss'],
})
export class ElementHostComponent implements OnChanges {
  @Input({ required: true })
  public element: DocumentElementResponse | undefined;

  @Input({ required: false })
  public options?: ElementHostOptions | undefined;

  @Input({ required: false })
  public hideIfNoElement: boolean = true;

  @ViewChild('visualization', { read: TemplateRef, static: true })
  protected templateVisualization: TemplateRef<{ data: Highcharts.Options }>;

  @ViewChild('valueDevelopment', { read: TemplateRef, static: true })
  protected templateValueDevelopment: TemplateRef<{ data: Highcharts.Options }>;

  @ViewChild('treemap', { read: TemplateRef, static: true })
  protected templateTreemap: TemplateRef<{ data: TreemapChartData }>;

  @ViewChild('doughnut', { read: TemplateRef, static: true })
  protected templateDoughnut: TemplateRef<{ data: Highcharts.Options }>;

  @ViewChild('text', { read: TemplateRef, static: true })
  protected templateText: TemplateRef<{ content: string; classes: string }>;

  @ViewChild('footnote', { read: TemplateRef, static: true })
  protected templateFootnote: TemplateRef<{ content: string[] }>;

  @ViewChild('solidTable', { read: TemplateRef, static: true })
  protected templateSolidTable: TemplateRef<{ data: Renderable }>;

  @ViewChild('simpleTable', { read: TemplateRef, static: true })
  protected templateSimpleTable: TemplateRef<{ data: Renderable }>;

  @ViewChild('tightTable', { read: TemplateRef, static: true })
  protected templateTightTable: TemplateRef<{ data: Renderable }>;

  @ViewChild('content', { static: true, read: ViewContainerRef })
  protected readonly viewContainerRef: ViewContainerRef;

  @HostBinding('attr.hidden')
  protected hidden: true | null = null;

  protected readonly legacyTransformer = legacyOptionsTransformer;
  protected readonly Highcharts = Highcharts;
  protected readonly Highstock = Highstock;

  private readonly embeddedViews: EmbeddedViewRef<unknown>[] = [];

  public ngOnChanges({ element, options }: SimpleChanges): void {
    if (this.hideIfNoElement) {
      this.hidden = this.element ? null : true;
    } else {
      this.hidden = null;
    }

    if (!options && !element) {
      return;
    }
    this.build();
  }

  private build(): void {
    this.clearEmbeddedViews();

    if (!this.element) {
      return;
    }

    switch (this.element.element.type) {
      case 'Visualization':
        this.insertVisualization();
        break;

      case 'Cell':
        this.insertCell();
        break;

      case 'Form':
      case 'XTable':
      case 'HTable':
      case 'VTable':
        this.insertTable();
        break;
    }
  }

  private insertVisualization(): void {
    if (this.element.renderable.type == 'TREEMAP') {
      this.insertTemplate(this.templateTreemap, {
        data: this.convertToTreemapData(this.element.renderable as Highcharts.Options),
      });
    }

    if (this.element.renderable.type == 'CIRCULAR_PIE') {
      this.insertTemplate(this.templateDoughnut, {
        data: this.element.renderable as Highcharts.Options
      });
      return;
    }

    if (this.options?.valueDevelopmentVisualization) {
      this.insertTemplate(this.templateValueDevelopment, {
        data: this.element.renderable as Highcharts.Options,
      });
      return;
    }

    this.insertTemplate(this.templateVisualization, { data: this.element.renderable as Highcharts.Options });
  }

  private insertCell(): void {
    const content: string = this.element.renderable.content;
    if (!content?.length) {
      this.hidden = true;
      return;
    }

    if (this.element.element.name === 'Fussnote') {
      this.insertFootnote();
      return;
    }

    const classes: string = this.options?.overrideCellCssClasses
      ? this.options.overrideCellCssClasses
      : getCellWidgetClasses(this.element.element).join(' ');
    this.insertTemplate(this.templateText, {
      content: this.element.renderable.content,
      classes: classes,
    });
  }

  private insertFootnote(): void {
    const content: string = `${getCellContentLabel(this.element.renderable.content)}`;

    const lines: string[] = content
      .replace('\r', '')
      .split(/<br>|<br\/>|\n/gm)
      .map(line => line.trim())
      .filter(line => line.length > 0);

    this.insertTemplate(this.templateFootnote, { content: lines });
  }

  private insertTable(): void {
    switch (this.options?.tableType ?? undefined) {
      case TableType.COMPLEX:
        throw new Error('Not implemented');
        break;

      case TableType.SIMPLE:
        this.insertTemplate(this.templateSimpleTable, { data: this.element.renderable });
        break;

      case TableType.TIGHT:
        this.insertTemplate(this.templateTightTable, { data: this.element.renderable });
        break;

      case TableType.SOLID:
      default:
        this.insertTemplate(this.templateSolidTable, { data: this.element.renderable });
    }
  }

  private clearEmbeddedViews(): void {
    for (const embed of this.embeddedViews) {
      embed.destroy();
    }
    this.embeddedViews.length = 0;
  }

  private insertTemplate<T>(template: TemplateRef<T>, context: T): void {
    this.embeddedViews.push(this.viewContainerRef.createEmbeddedView(template, context));
  }

  private convertToTreemapData(arg0: Options): TreemapChartData {
    return {
      series: (arg0.series[0] as SeriesTreemapOptions).data as TreemapChartDataSeries[],
      keys: arg0.series[0]?.custom?.['key'] as string[],
    };
  }
}
